/* eslint-disable simple-import-sort/imports, sort-imports -- lodash `isFinite as lodashIsFinite` vs conflicting member-order rules */
import {
    castArray,
    compact,
    flattenDeep,
    isArray,
    isFinite as lodashIsFinite,
    isNil,
    isPlainObject,
    isString,
    startCase,
    toNumber,
    uniq,
} from 'lodash';
/* eslint-enable simple-import-sort/imports, sort-imports */

import { isMidnightPurchaseOrderDateTime, parsePurchaseOrderDate } from './purchaseOrderDateUtils';

export const FALLBACK_TEXT = '—';
export const DELIVERY_TYPE_PICKUP = 'PICK UP';
export const DELIVERY_TYPE_DELIVERED = 'DELIVERED';

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

/**
 * Returns a human-friendly fallback string when the provided value is nullish or empty
 * otherwise returns the trimmed stringified value.
 *
 * @param {*} value Candidate value.
 * @returns {string} Normalized string or the fallback token.
 */
export const normalizeOptionalText = (value) => {
    if (value === null || value === undefined) return FALLBACK_TEXT;
    const normalized = String(value).trim();
    return normalized || FALLBACK_TEXT;
};

/**
 * Formats a currency-like input into USD using Intl.NumberFormat, falling back when invalid.
 *
 * @param {number|string|null|undefined} value Value to format.
 * @returns {string} Currency string or fallback.
 */
export const formatCurrency = (value) => {
    const candidate = typeof value === 'string' ? value.replace(/,/g, '') : value;
    const numericValue = toNumber(candidate);
    if (!lodashIsFinite(numericValue)) return FALLBACK_TEXT;
    return currencyFormatter.format(numericValue);
};

/**
 * Formats a numeric value with localized separators and optional fraction digit overrides.
 *
 * @param {number|string|null|undefined} value Value to format.
 * @param {{maximumFractionDigits?: number, minimumFractionDigits?: number}} [options] Formatting overrides.
 * @returns {string} Formatted number or fallback.
 */
export const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined || value === '') return FALLBACK_TEXT;

    const numericValue = toNumber(value);
    if (!lodashIsFinite(numericValue)) {
        return normalizeOptionalText(value);
    }

    const { maximumFractionDigits, minimumFractionDigits } = options;

    const resolvedMaximumFractionDigits = typeof maximumFractionDigits === 'number' ? maximumFractionDigits : 2;
    let resolvedMinimumFractionDigits = 2;
    if (typeof minimumFractionDigits === 'number') {
        resolvedMinimumFractionDigits = minimumFractionDigits;
    } else if (Number.isInteger(numericValue)) {
        resolvedMinimumFractionDigits = 0;
    }

    return numericValue.toLocaleString('en-US', {
        maximumFractionDigits: resolvedMaximumFractionDigits,
        minimumFractionDigits: resolvedMinimumFractionDigits,
    });
};

/**
 * Formats a delivery type identifier into title case for display.
 *
 * @param {string|null|undefined} value Delivery type value.
 * @returns {string} Formatted delivery type or fallback.
 */
export const formatDeliveryType = (value) => {
    if (!value) return FALLBACK_TEXT;
    if (String(value).toUpperCase() === DELIVERY_TYPE_DELIVERED) return 'Vendor Delivered';
    return startCase(String(value).toLowerCase());
};

/**
 * Resolves buyer representations (strings or objects) into normalized name strings.
 *
 * @param {*} value Buyers value from the backend.
 * @returns {string[]} Array of normalized buyer names.
 */
export const resolveBuyerNames = (value) => {
    if (!value) return [];

    if (isArray(value)) {
        return compact(
            value.map((item) => {
                if (isString(item)) return item.trim();
                if (isPlainObject(item)) {
                    if (isString(item.name) && item.name.trim()) return item.name.trim();
                    if (isString(item.fullName) && item.fullName.trim()) return item.fullName.trim();
                }
                return null;
            }),
        );
    }

    if (isString(value) && value.trim()) {
        return [value.trim()];
    }

    if (isPlainObject(value) && isString(value.name) && value.name.trim()) {
        return [value.name.trim()];
    }

    return [];
};

/**
 * Formats the buyers field by joining normalized buyer names or returning the fallback.
 *
 * @param {*} value Buyers value from the backend.
 * @returns {string} Comma-separated buyer names or fallback.
 */
export const formatBuyerNames = (value) => {
    const names = resolveBuyerNames(value);
    if (!names.length) return FALLBACK_TEXT;
    return names.join(', ');
};

/**
 * Normalizes a value to an uppercase trimmed string, returning empty when falsy.
 *
 * @param {*} value Value to normalize.
 * @returns {string} Uppercase value or empty string.
 */
export const normalizeToUpperCase = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toUpperCase();
};

/**
 * Normalizes a heterogeneous set of inputs (arrays, scalars, numbers) into a unique array of
 * non-empty strings suitable for select or multi-select widgets.
 *
 * @param {...*} inputs Candidate values.
 * @returns {string[]} Unique list of normalized strings.
 */
export const normalizeStringList = (...inputs) => {
    if (!inputs.length) return [];

    const flattened = flattenDeep(inputs.map((input) => castArray(input)));

    const normalized = flattened
        .map((value) => {
            if (value === null || value === undefined) return null;
            if (isString(value)) {
                const trimmed = value.trim();
                return trimmed || null;
            }
            if (typeof value === 'number' && lodashIsFinite(value)) {
                return String(value);
            }

            return null;
        })
        .filter(Boolean);

    return uniq(normalized);
};

/**
 * Normalizes option objects for select-style components by coercing their values into trimmed strings
 * and pruning empty entries to ensure compatibility with UI widgets that expect string identifiers.
 *
 * @param {Array<{label?: React.ReactNode, value?: string|number}>} [options=[]] Raw option definitions.
 * @returns {Array<{label: React.ReactNode, value: string}>} Sanitized option list safe for UI components.
 */
export const sanitizeOptions = (options = []) =>
    (options ?? [])
        .map((option) => {
            if (!option || option.value === null || option.value === undefined) return null;
            const rawValue = option.value;
            const normalizedValue = typeof rawValue === 'string' ? rawValue.trim() : String(rawValue);
            if (!normalizedValue) return null;

            return {
                label: option.label ?? normalizedValue,
                value: normalizedValue,
            };
        })
        .filter(Boolean);

/**
 * Formats a temperature value into a display string, appending °F when appropriate.
 *
 * @param {*} value Temperature value.
 * @returns {string} Formatted temperature or fallback.
 */
export const formatTemperature = (value) => {
    if (value === null || value === undefined) return FALLBACK_TEXT;
    if (typeof value === 'number') return `${value}°F`;

    const normalized = String(value).trim();
    if (!normalized) return FALLBACK_TEXT;
    if (normalized.includes('°')) return normalized;

    const numericValue = toNumber(normalized);
    if (lodashIsFinite(numericValue)) return `${numericValue}°F`;
    return normalized;
};

/**
 * Formats a min/max temperature pair into a ranged display string.
 *
 * @param {*} minValue Minimum temperature.
 * @param {*} maxValue Maximum temperature.
 * @returns {string} Formatted temperature range or fallback.
 */
export const formatTemperatureRange = (minValue, maxValue) => {
    const minLabel = formatTemperature(minValue);
    const maxLabel = formatTemperature(maxValue);

    if (minLabel === FALLBACK_TEXT && maxLabel === FALLBACK_TEXT) {
        return FALLBACK_TEXT;
    }

    return `${minLabel} - ${maxLabel}`;
};

/**
 * Determines if a purchase order is considered a cross-dock based on ID values.
 *
 * @param {Object|null} purchaseOrder Purchase order object.
 * @returns {boolean} True when cross dock ID is present and positive.
 */
export const isCrossDockPurchaseOrder = (purchaseOrder) => {
    if (isNil(purchaseOrder)) return false;

    const crossDockValue = purchaseOrder.crossDockID ?? purchaseOrder.crossDockId;
    if (crossDockValue === null || crossDockValue === undefined) return false;

    const numericCrossDock = toNumber(crossDockValue);
    if (!lodashIsFinite(numericCrossDock)) return false;

    return numericCrossDock > 0;
};

/**
 * Determines whether the purchase order is considered late based on scheduled delivery.
 *
 * @param {Object|null} purchaseOrder Purchase order object.
 * @param {*} [scheduledDeliveryValue=purchaseOrder?.scheduledDeliveryDate] Optional override date.
 * @returns {boolean} True when late, else false.
 */
export const isLatePurchaseOrder = (purchaseOrder, scheduledDeliveryValue = purchaseOrder?.scheduledDeliveryDate) => {
    if (isNil(purchaseOrder) || !isMidnightPurchaseOrderDateTime(scheduledDeliveryValue)) return false;

    const status = String(purchaseOrder.status ?? '').toUpperCase();
    if (status !== 'PLACED') return false;

    const deliveryDate = parsePurchaseOrderDate(scheduledDeliveryValue);
    if (!deliveryDate) return false;

    const now = new Date();
    const diffInDays = (deliveryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diffInDays < 8;
};

/**
 * Determines whether the purchase order should be flagged as priority.
 *
 * @param {Object|null} purchaseOrder Purchase order object.
 * @param {*} [scheduledDeliveryValue=purchaseOrder?.scheduledDeliveryDate] Optional override date.
 * @returns {boolean} True when priority, else false.
 */
export const isPriorityPurchaseOrder = (
    purchaseOrder,
    scheduledDeliveryValue = purchaseOrder?.scheduledDeliveryDate,
) => {
    if (isNil(purchaseOrder)) return false;
    if (purchaseOrder.priority === true) return true;
    return isLatePurchaseOrder(purchaseOrder, scheduledDeliveryValue);
};

/**
 * Returns the confirmation timestamp for a purchase order, if present.
 * Confirmed POs have a non-null timestamp on `confirmed` (or `confirmedAt`).
 *
 * @param {Object|null|undefined} purchaseOrder Purchase order object.
 * @returns {string|number|null} Confirmation timestamp, or null when absent/empty.
 */
export const getPurchaseOrderConfirmedAt = (purchaseOrder) => {
    if (purchaseOrder == null || typeof purchaseOrder !== 'object') return null;

    const confirmed = Object.prototype.hasOwnProperty.call(purchaseOrder, 'confirmed')
        ? purchaseOrder.confirmed
        : Object.prototype.hasOwnProperty.call(purchaseOrder, 'confirmedAt')
          ? purchaseOrder.confirmedAt
          : undefined;

    if (confirmed === undefined) return null;
    if (confirmed == null) return null;
    if (String(confirmed).trim() === '') return null;
    return confirmed;
};

/**
 * Determines whether a purchase order has been confirmed.
 * Confirmed POs have a non-null timestamp on `confirmed`.
 *
 * @param {Object|null|undefined} purchaseOrder Purchase order object.
 * @returns {boolean} True when confirmed, else false.
 */
export const isPurchaseOrderConfirmed = (purchaseOrder) => getPurchaseOrderConfirmedAt(purchaseOrder) != null;

export { isNotSentToVendorPurchaseOrder } from './purchaseOrderDateUtils';
